<template>
  <div class="grid grid-cols-5 mt-3">
    <div class="col-start-3 col-span-5">
      <ChatGPTActionPlaceholder
        :label="label"
        :is-product-recommendation="!!productPreview"
        :focus="focus"
        :error="isErrorBubbleFocused && actionId === error.actionId"
        @click="selectChatGPTBubble"
      />

      <!-- PRO-740: what a product answer looks like with its current settings — invented products, see PRO-741 -->
      <div v-if="productPreview" class="mt-2 cursor-pointer" @click="selectChatGPTBubble">
        <span class="block mb-1 text-xs text-gray">{{ previewCaption }}</span>
        <div v-if="productPreview.emailText" class="p-3 rounded bg-soft text-sm text-gray-dark whitespace-pre-line">
          {{ productPreview.emailText }}
        </div>
        <!-- the preview's root is height: 100% (it fills the history bubble); here it must size to its cards -->
        <carousel-preview v-else style="height: auto" :payload="previewCards" :layout="productPreview.layout"/>
        <div v-if="productPreview.chips.length" class="flex flex-wrap justify-end">
          <span v-for="chip in productPreview.chips"
                :key="chip"
                class="border border-solid rounded-full ml-1 mt-2 px-3 py-2 text-sm border-primary text-primary"
          >
            {{ chip }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'
import previewModule from '@/store/modules/preview.module'
import ChatGPTActionPlaceholder from '@/components/common/ChatGPTActionPlaceholder.vue'
import CarouselPreview from '@/components/preview/CarouselPreview.vue'
import botModule from '@/store/modules/bot.module'
import { loadPreviewProducts } from '@/services/amio/product-preview.service'
import { SAMPLE_PRODUCT_CARDS } from '@/constants/product-recommendation.constants'
import { isProductRecommendationAction } from '@/utils/chat-gpt-action.utils'

// Example quick filters: the brands of the shown products — what the model most often offers. The real chips are
// written by the model per conversation.
const exampleFilters = cards => [...new Set(cards.map(card => card.subtitle).filter(Boolean))].slice(0, 2)

// An email answer has no cards or chips — the products are written into the reply
const emailTextFor = cards => cards
  .map(card => {
    const price = (card.product || card.details?.product)?.price
    return `• ${card.title}${card.subtitle ? ` (${card.subtitle})` : ''}${price ? ` — ${price}` : ''}`
  })
  .join('\n')

export default {
  name: 'ChatGPTBubble',
  components: { ChatGPTActionPlaceholder, CarouselPreview },

  data() {
    // PRO-740: real products from the answer's knowledge base; null until they arrive (samples meanwhile)
    return { realCards: null }
  },

  props: {
    stateId: { type: String, required: true },
    actionId: { type: String, required: true },
    isTemplate: { type: Boolean, required: true },
    focus: { type: Boolean, required: true },
    inNestedCondition: { type: Boolean, default: false }
  },

  computed: {
    bubbleSelected: chatBubbleModule.bubbleSelected,
    error: previewModule.error,

    isErrorBubbleFocused() {
      return chatBubbleModule.isErrorBubbleFocused()
    },

    action() {
      return conversationModule.getAction(this.stateId, this.actionId)
    },

    isEmailBot() {
      return botModule.isEmailBot()
    },

    previewCards() {
      return this.realCards?.length ? this.realCards : SAMPLE_PRODUCT_CARDS
    },

    previewCaption() {
      return this.realCards?.length ? 'Preview · products from your knowledge base' : 'Preview · sample products'
    },

    // What the real products depend on, as a string: a settings toggle rebuilds the action object, and an object
    // here would refetch (and flash the samples) on every toggle. Only a new knowledge base or market changes it.
    previewSourceKey() {
      if (!isProductRecommendationAction(this.action) || !this.action.indexId) return ''
      const bot = botModule.getBot() || {}
      return JSON.stringify({ indexId: this.action.indexId, indexType: this.action.indexType, marketId: bot.market_id, language: bot.language })
    },

    label() {
      return isProductRecommendationAction(this.action) ? 'Product recommendations' : 'AI answer'
    },

    // null for an AI answer. Automatic previews as rows — what the engine picks for an ordinary list.
    productPreview() {
      if (!isProductRecommendationAction(this.action)) return null
      if (this.isEmailBot) return { chips: [], emailText: emailTextFor(this.previewCards) }

      const { layout, quickFilters, showContinue } = this.action.productRecommendation
      return {
        layout: layout || 'rows',
        chips: [...(quickFilters === false ? [] : exampleFilters(this.previewCards)), ...(showContinue ? ['More products'] : [])]
      }
    }
  },

  watch: {
    previewSourceKey: {
      immediate: true,
      async handler(key) {
        this.realCards = null
        if (!key) return

        const cards = await loadPreviewProducts(JSON.parse(key))
        // a later knowledge-base change may have overtaken this request
        if (key === this.previewSourceKey) this.realCards = cards
      }
    }
  },

  methods: {
    selectChatGPTBubble() {
      chatBubbleModule.selectChatGPTBubble(this.stateId, this.actionId, this.isTemplate, this.inNestedCondition)
    }
  }
}
</script>
